import type { EngageTarget } from "../lib/engage";
import { useUnits } from "../hooks/useUnits";
import { fmtWeight } from "../lib/units";

// Self-set daily target ring (M6) — HomeDashboard header. Purely
// presentational: tapping opens TargetSheet, which owns the set/clear UI.

const SIZE = 132;
const STROKE = 10;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

export function TargetRing({
  todayGrams, target, onTap,
}: {
  todayGrams: number;
  target: EngageTarget;
  onTap: () => void;
}) {
  const units = useUnits();
  const hasTarget = target.grams != null;
  const pct = hasTarget ? Math.min(100, Math.max(0, target.progress_pct ?? 0)) : 0;
  const met = hasTarget && pct >= 100;
  const dash = (pct / 100) * CIRC;

  return (
    <button
      type="button"
      className={`target-ring${hasTarget ? "" : " ghost"}${met ? " met" : ""}`}
      data-testid="target-ring"
      data-state={hasTarget ? (met ? "met" : "in-progress") : "no-target"}
      onClick={onTap}
    >
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <circle
          cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none"
          stroke="var(--border)" strokeWidth={STROKE}
          strokeDasharray={hasTarget ? undefined : "4 7"}
        />
        {hasTarget && (
          <circle
            cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none"
            stroke={met ? "var(--primary)" : "var(--primary-strong)"} strokeWidth={STROKE}
            strokeDasharray={`${dash} ${CIRC - dash}`} strokeLinecap="round"
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        )}
      </svg>
      <div className="target-ring-label">
        <div className="target-ring-grams">{fmtWeight(Math.round(todayGrams), units)}</div>
        <div className="target-ring-sub">
          {hasTarget ? (met ? "target met" : `of ${fmtWeight(Math.round(target.grams as number), units)}`) : "set a target"}
        </div>
      </div>
    </button>
  );
}
